/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { LogOut, User as UserIcon, Palette, Link as LinkIcon, Copy, Check, UploadCloud, X, Sparkles, Building2, BookOpen, Award, RefreshCw, Smartphone, AlertTriangle, ChevronDown, Wrench } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { getSavedCompanies } from '../utils/saasUtils';
import { downloadInsumedicManualPdf } from '../utils/manualPdfGenerator';
import { downloadInsumedicCertificadoPdf } from '../utils/certPdfGenerator';
import MobileInstallModal from './MobileInstallModal';
import PharmaTraceLogo from './PharmaTraceLogo';

interface HeaderProps {
  currentUser: User | null;
  onLogout: () => void;
  isSyncing?: boolean;
  syncStatus?: 'success' | 'error' | 'syncing' | 'idle';
  onForceSync?: () => void;
  renewalAlertDays?: number | null;
  onOpenRenewalModal?: () => void;
}

export default function Header({ 
  currentUser, 
  onLogout, 
  isSyncing = false, 
  syncStatus = 'idle', 
  onForceSync,
  renewalAlertDays,
  onOpenRenewalModal
}: HeaderProps) {
  const [showBrandingModal, setShowBrandingModal] = useState(false);
  const [showMobileInstallModal, setShowMobileInstallModal] = useState(false);
  const [showResourcesMenu, setShowResourcesMenu] = useState(false);
  const [customLogo, setCustomLogo] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Loaded company NIT to build the personalized link
  const [companyNit, setCompanyNit] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Load custom logo matching the logged in company on mount or when currentUser changes
  useEffect(() => {
    if (currentUser?.companyId) {
      const storedLogo = localStorage.getItem(`custom_logo_${currentUser.companyId}`);
      setCustomLogo(storedLogo);

      // Find actual NIT and Name
      const companies = getSavedCompanies();
      const matchComp = companies.find(c => c.id === currentUser.companyId);
      if (matchComp) {
        setCompanyNit(matchComp.nit);
        setCompanyName(matchComp.nombreEmpresa);
      }
    } else {
      setCustomLogo(null);
    }
  }, [currentUser]);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage('');
    setSuccessMessage('');
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Por favor seleccione un archivo de imagen válido (PNG, JPG, SVG).');
      return;
    }

    // Limit files to 3MB for localStorage safety
    if (file.size > 3 * 1024 * 1024) {
      setErrorMessage('El archivo de imagen excede el límite de 3MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = reader.result as string;
      if (currentUser?.companyId) {
        localStorage.setItem(`custom_logo_${currentUser.companyId}`, base64Data);
        setCustomLogo(base64Data);
        setSuccessMessage('¡Logo corporativo guardado y aplicado de forma exitosa!');
        // Dispatch an event to notify other components (like AuthScreen or same page)
        window.dispatchEvent(new Event('storage'));
      }
    };
    reader.onerror = () => {
      setErrorMessage('Hubo un error al leer el archivo.');
    };
    reader.readAsDataURL(file);
  };

  const handleResetLogo = () => {
    if (currentUser?.companyId) {
      localStorage.removeItem(`custom_logo_${currentUser.companyId}`);
      setCustomLogo(null);
      setSuccessMessage('Logo restablecido al diseño predeterminado.');
      window.dispatchEvent(new Event('storage'));
    }
  };

  // Build the dynamic custom access link
  const parsedNit = companyNit ? companyNit.trim() : currentUser?.cedula || '';
  const personalizedLink = parsedNit 
    ? `${window.location.origin}?nit=${encodeURIComponent(parsedNit)}` 
    : window.location.origin;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(personalizedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Default logo is the Quality platform logo
  const defaultLogo = "/src/assets/images/quality_logo_1779641796639.png";
  const logoSrc = customLogo || defaultLogo;

  // Feature toggle to disable portal personalization as requested, keeping it in the application's source code
  const isPersonalizacionEnabled = false;

  return (
    <>
      {/* Banda Institucional del Software (PHARMA TRACE) */}
      <div className="w-full bg-[#071A36] text-white border-b border-[#1C4F8A]/50 z-35 relative shadow-md select-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-[68px]">
            {/* Lado Izquierdo: Logo oficial + PHARMA TRACE + Subtítulo */}
            <PharmaTraceLogo className="h-10 sm:h-11" showText={true} variant="light" />

            {/* Lado Derecho: Versión del Software */}
            <div className="flex items-center space-x-3 text-right">
              <div className="hidden sm:flex flex-col items-end leading-tight">
                <span className="text-xs font-black tracking-wide text-white font-sans uppercase">
                  Versión 5.0
                </span>
                <span className="text-[10px] font-mono text-sky-300 uppercase tracking-wider font-bold">
                  Enterprise Edition
                </span>
              </div>
              <span className="sm:hidden text-[9px] font-black text-sky-300 bg-[#0D274F] px-2 py-1 rounded border border-[#1C4F8A]/80 uppercase tracking-wider">
                v5.0 Enterprise
              </span>
            </div>
          </div>
        </div>
      </div>

      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30 shadow-xs transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center font-sans">
            {/* Brand Title (Logo removed per user request) */}
            <div className="flex flex-col justify-center">
              <span className="text-[11px] md:text-sm font-sans font-black text-slate-850 dark:text-slate-100 tracking-tight block uppercase leading-tight">
                {companyName || "Quality Procesos"}
              </span>
              <span className="text-[9px] md:text-[10px] font-mono text-slate-400 dark:text-slate-400 tracking-wider block uppercase font-bold mt-0.5">
                Seguimiento de Unidosis y GRC
              </span>
            </div>
 
            {/* Logged in user information & branding control */}
            {currentUser && (
              <div className="flex items-center space-x-3 md:space-x-4">
                {/* Renewal Warning Badge if <= 5 days remaining */}
                {renewalAlertDays !== undefined && renewalAlertDays !== null && renewalAlertDays <= 5 && onOpenRenewalModal && (
                  <button
                    onClick={onOpenRenewalModal}
                    className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-black text-amber-950 bg-amber-400 hover:bg-amber-350 border border-amber-500 transition-all cursor-pointer shadow-3xs animate-bounce"
                    title="Aviso de renovación de licencia - Click para ver detalles"
                    id="btn-header-renewal-alert"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-950 fill-amber-200" />
                    <span>Renovación ({renewalAlertDays <= 0 ? 'Hoy' : `${renewalAlertDays}d`})</span>
                  </button>
                )}

                {/* Menú Desplegable de Recursos y Utilidades (4 Botones Unificados) */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowResourcesMenu(!showResourcesMenu)}
                    className="flex items-center space-x-2 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#071A36] text-white hover:bg-[#0D274F] border border-[#1C4F8A] transition-all cursor-pointer shadow-3xs"
                    id="btn-resources-menu"
                    title="Centro de Recursos, Sincronización Nube, App Celular (APK) y Documentos PDF"
                  >
                    <Wrench className="h-3.5 w-3.5 text-sky-300" />
                    <span className="hidden sm:inline">Recursos & Utilidades</span>
                    <span className="sm:hidden">Recursos</span>
                    <ChevronDown className={`h-3 w-3 text-slate-300 transition-transform duration-200 ${showResourcesMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Popover Dropdown Card */}
                  <AnimatePresence>
                    {showResourcesMenu && (
                      <>
                        {/* Backdrop Click Off Overlay */}
                        <div 
                          className="fixed inset-0 z-40 bg-black/10" 
                          onClick={() => setShowResourcesMenu(false)} 
                        />

                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: -8 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: -8 }}
                          transition={{ duration: 0.15 }}
                          className="absolute right-0 mt-2 w-72 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 p-2.5 z-50 space-y-1.5 select-none"
                        >
                          <div className="px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-800">
                            <span className="text-[10px] font-black uppercase tracking-wider text-blue-800 dark:text-blue-300 block font-sans">
                              PHARMA TRACE • Recursos
                            </span>
                            <span className="text-[10px] text-slate-400 dark:text-slate-500 font-sans block">
                              Acceso a utilidades, sincronización y descargas
                            </span>
                          </div>

                          {/* 1. Sincronizar Nube */}
                          {onForceSync && (
                            <button
                              onClick={() => {
                                onForceSync();
                                setShowResourcesMenu(false);
                              }}
                              disabled={isSyncing}
                              className={`w-full flex items-center justify-between p-2 rounded-xl text-xs font-semibold transition-all cursor-pointer text-left ${
                                isSyncing
                                  ? 'bg-slate-50 text-slate-400 cursor-not-allowed'
                                  : 'hover:bg-blue-50 dark:hover:bg-blue-950/40 text-slate-700 dark:text-slate-200'
                              }`}
                              id="btn-dropdown-sync"
                              title="Sincronización en Tiempo Real Cloud con Google Firestore desde cualquier dispositivo, ciudad o red IP"
                            >
                              <div className="flex items-center space-x-2.5">
                                <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                                  <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                                </div>
                                <div className="flex flex-col">
                                  <span className="font-bold text-slate-800 dark:text-slate-100 text-xs">
                                    {isSyncing ? 'Sincronizando Nube...' : 'Sincronizar Nube (Global)'}
                                  </span>
                                  <span className="text-[10px] text-slate-400 font-normal">
                                    {syncStatus === 'success' ? 'Conectado Realtime Multi-dispositivo' : 'Base de Datos Cloud (Cualquier IP / Dispositivo)'}
                                  </span>
                                </div>
                              </div>
                              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-300 font-bold uppercase">
                                Realtime
                              </span>
                            </button>
                          )}

                          {/* 2. App Celular (APK) */}
                          <button
                            onClick={() => {
                              setShowMobileInstallModal(true);
                              setShowResourcesMenu(false);
                            }}
                            className="w-full flex items-center justify-between p-2 rounded-xl text-xs font-semibold hover:bg-blue-50 dark:hover:bg-blue-950/40 text-slate-700 dark:text-slate-200 transition-all cursor-pointer text-left"
                            id="btn-dropdown-mobile"
                          >
                            <div className="flex items-center space-x-2.5">
                              <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                                <Smartphone className="h-4 w-4" />
                              </div>
                              <div className="flex flex-col">
                                <span className="font-bold text-slate-800 dark:text-slate-100 text-xs">
                                  App Celular (APK / PWA)
                                </span>
                                <span className="text-[10px] text-slate-400 font-normal">
                                  Instalar en Android / iPhone
                                </span>
                              </div>
                            </div>
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-300 font-bold uppercase">
                              APK
                            </span>
                          </button>

                          {/* 3. Certificado de Funcionalidad */}
                          <button
                            onClick={() => {
                              downloadInsumedicCertificadoPdf();
                              setShowResourcesMenu(false);
                            }}
                            className="w-full flex items-center justify-between p-2 rounded-xl text-xs font-semibold hover:bg-amber-50 dark:hover:bg-amber-950/40 text-slate-700 dark:text-slate-200 transition-all cursor-pointer text-left"
                            id="btn-dropdown-certificado"
                          >
                            <div className="flex items-center space-x-2.5">
                              <div className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">
                                <Award className="h-4 w-4" />
                              </div>
                              <div className="flex flex-col">
                                <span className="font-bold text-slate-800 dark:text-slate-100 text-xs">
                                  Certificado Técnico
                                </span>
                                <span className="text-[10px] text-slate-400 font-normal">
                                  Certificado de Funcionalidad PDF
                                </span>
                              </div>
                            </div>
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-300 font-bold uppercase">
                              PDF
                            </span>
                          </button>

                          {/* 4. Manual de Usuario (PDF) */}
                          <button
                            onClick={() => {
                              downloadInsumedicManualPdf();
                              setShowResourcesMenu(false);
                            }}
                            className="w-full flex items-center justify-between p-2 rounded-xl text-xs font-semibold hover:bg-blue-50 dark:hover:bg-blue-950/40 text-slate-700 dark:text-slate-200 transition-all cursor-pointer text-left"
                            id="btn-dropdown-manual"
                          >
                            <div className="flex items-center space-x-2.5">
                              <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                                <BookOpen className="h-4 w-4" />
                              </div>
                              <div className="flex flex-col">
                                <span className="font-bold text-slate-800 dark:text-slate-100 text-xs">
                                  Manual de Usuario
                                </span>
                                <span className="text-[10px] text-slate-400 font-normal">
                                  Manual Técnico v5.0 en PDF
                                </span>
                              </div>
                            </div>
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-300 font-bold uppercase">
                              PDF
                            </span>
                          </button>

                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>

                {/* Branding Personalization Button (Available for corporate users) */}
                {isPersonalizacionEnabled && currentUser.companyId && currentUser.username !== 'admin' && (
                  <button
                    onClick={() => {
                      setErrorMessage('');
                      setSuccessMessage('');
                      setShowBrandingModal(true);
                    }}
                    className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-teal-800 bg-teal-50 hover:bg-teal-100/80 border border-teal-200/60 transition-all cursor-pointer shadow-3xs"
                    title="Branding y Acceso Personalizado"
                  >
                    <Palette className="h-3.5 w-3.5" />
                    <span className="hidden md:inline">Personalizar Portal</span>
                  </button>
                )}

                <div className="flex items-center space-x-3 bg-slate-50 dark:bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-100 dark:border-slate-700">
                  <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-200">
                    <UserIcon className="h-4.5 w-4.5" />
                  </div>
                  <div className="text-left hidden sm:block">
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-100 block leading-tight">
                      {currentUser.nombreCompleto}
                    </span>
                    <span className="text-[10px] font-mono text-slate-500 dark:text-slate-400 lowercase bg-slate-200/60 dark:bg-slate-700 px-1 rounded inline-block mt-0.5">
                      u: {currentUser.username}
                    </span>
                    <span className="text-[10px] font-sans text-emerald-600 dark:text-emerald-400 block leading-tight font-bold mt-0.2">
                      {currentUser.cargo}
                    </span>
                  </div>
                </div>

                <button
                  onClick={onLogout}
                  className="flex items-center justify-center h-9 px-3 text-slate-500 hover:text-red-650 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100 focus:outline-hidden"
                  title="Cerrar Sesión"
                  id="btn-logout"
                >
                  <LogOut className="h-4.5 w-4.5 mr-1.5" />
                  <span className="text-xs font-semibold">Salir</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Brand Personalization Modal */}
      <AnimatePresence>
        {showBrandingModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto" id="branding-modal-container">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity"
              onClick={() => setShowBrandingModal(false)}
            ></div>

            {/* Modal Wrapper */}
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                transition={{ duration: 0.2 }}
                className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all w-full max-w-lg border border-slate-200"
              >
                {/* Header */}
                <div className="bg-slate-50/50 px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <Palette className="h-5 w-5 text-teal-700" />
                    <div>
                      <h3 className="text-sm font-black text-slate-900 font-sans tracking-tight">
                        BRANDING Y ACCESO PERSONALIZADO
                      </h3>
                      <p className="text-[10px] text-slate-400 font-sans mt-0.5">
                        Personalice la experiencia corporativa para su equipo y auxiliares
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowBrandingModal(false)}
                    className="p-1 px-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-150 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  {/* Status Alerts */}
                  {successMessage && (
                    <div className="p-3 bg-emerald-50 border border-emerald-250 text-emerald-850 rounded-xl text-xs font-sans font-semibold flex items-center space-x-2">
                      <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                      <span>{successMessage}</span>
                    </div>
                  )}
                  {errorMessage && (
                    <div className="p-3 bg-rose-50 border border-rose-250 text-rose-850 rounded-xl text-xs font-sans font-semibold flex items-center space-x-2">
                      <X className="h-4 w-4 text-rose-600 shrink-0" />
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  {/* LOGO CHARGER */}
                  <div className="space-y-2.5">
                    <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider">
                      1. LOGO DE LA EMPRESA
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center p-4 bg-slate-50 border border-slate-200 rounded-xl">
                      {/* Preview screen */}
                      <div className="flex flex-col items-center justify-center p-3 bg-white rounded-lg border border-slate-200 max-h-24 h-24">
                        <img 
                          src={logoSrc} 
                          alt="Previsualización" 
                          className="max-h-18 object-contain"
                        />
                        {!customLogo && (
                          <span className="text-[8px] bg-slate-100 text-slate-500 px-1 rounded font-sans mt-1">
                            Predeterminado
                          </span>
                        )}
                        {customLogo && (
                          <span className="text-[8px] bg-teal-100 text-teal-800 px-1 rounded font-sans mt-1 font-bold">
                            Personalizado
                          </span>
                        )}
                      </div>

                      {/* Upload Controls */}
                      <div className="sm:col-span-2 space-y-2 text-center sm:text-left">
                        <div className="flex items-center justify-center sm:justify-start space-x-2">
                          <label className="px-3 py-1.5 bg-slate-850 hover:bg-slate-950 text-white rounded-lg text-xs font-bold transition-all cursor-pointer select-none inline-flex items-center space-x-1.5 shadow-3xs">
                            <UploadCloud className="h-3.5 w-3.5" />
                            <span>Cargar Imagen</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleLogoUpload}
                              className="hidden"
                            />
                          </label>

                          {customLogo && (
                            <button
                              onClick={handleResetLogo}
                              className="px-2.5 py-1.5 border border-slate-200 hover:bg-slate-100 text-slate-500 hover:text-slate-800 rounded-lg text-xs font-bold transition-all cursor-pointer"
                            >
                              Restablecer
                            </button>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 font-sans leading-tight">
                          Formatos aceptados: PNG, JPG, SVG. Límite de tamaño: 3MB. El logo se mostrará en el encabezado general del sistema.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* PERSONALIZED ENLACE */}
                  <div className="space-y-2.5 pt-2">
                    <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider">
                      2. ENLACE GENERAL DE ACCESO EXCLUSIVO
                    </label>
                    <div className="space-y-2">
                      <p className="text-[11px] text-slate-500 font-gray-500 font-sans leading-relaxed">
                        Copie y comparta este enlace con todo el personal corporativo, técnicos y auxiliares de su empresa.
                      </p>
                      
                      <div className="flex items-center space-x-2 bg-slate-100 p-2.5 rounded-xl border border-slate-200/80">
                        <span className="text-[11px] font-mono text-slate-600 truncate flex-1 select-all">
                          {personalizedLink}
                        </span>
                        <button
                          onClick={handleCopyLink}
                          className={`p-1.5 rounded-lg cursor-pointer transition-all ${
                            copied 
                              ? 'bg-emerald-500 text-white' 
                              : 'bg-white text-slate-600 hover:bg-slate-150 border border-slate-200'
                          }`}
                          title="Copiar Enlace"
                        >
                          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      </div>

                      <div className="p-3 bg-teal-50/50 border border-teal-200/60 rounded-xl space-y-1.5">
                        <div className="flex items-center space-x-1.5 text-teal-850">
                          <Sparkles className="h-3.5 w-3.5 animate-pulse" />
                          <span className="text-[10px] font-extrabold uppercase font-sans tracking-wide">Ventajas del Acceso Personalizado:</span>
                        </div>
                        <ul className="list-disc pl-4 text-[10px] text-slate-550 space-y-1 font-sans">
                          <li>
                            El formulario de acceso se pre-cargará de forma automática con el **NIT de la empresa** ({companyNit || 'No definido'}), minimizando errores de transcripción.
                          </li>
                          <li>
                            La pantalla de entrada mostrará el **logo corporativo** cargado, identificando formalmente su portal de trazabilidad.
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-slate-50/50 px-6 py-3 border-t border-slate-100 flex justify-end">
                  <button
                    onClick={() => setShowBrandingModal(false)}
                    className="px-4 py-2 bg-slate-900 border border-slate-200 hover:bg-slate-950 text-white rounded-xl text-xs font-bold transition-all cursor-pointer"
                  >
                    Entendido / Cerrar
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Mobile App Install Modal */}
      <MobileInstallModal
        isOpen={showMobileInstallModal}
        onClose={() => setShowMobileInstallModal(false)}
      />
    </>
  );
}
